import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { SignatureCanvasHandle } from "./SignatureCanvas";

interface Props {
  /** Live name typed by the user (kept in sync from the parent). */
  name: string;
  className?: string;
}

const FONT_FAMILY = `"Dancing Script", "Snell Roundhand", "Apple Chancery", cursive`;
const TEXT_COLOR = "#0a1430";

/**
 * Renders the user's typed name in a cursive (Dancing Script) face on a
 * canvas — visually mimicking a signature — and exposes the same handle
 * shape as the draw-mode SignatureCanvas so the parent can flip between
 * modes without changing its calling code.
 *
 * The PNG export is rendered at a high pixel density (3x DPR floor) so the
 * stamped signature stays crisp on the PDF.
 */
export const TypedSignature = forwardRef<SignatureCanvasHandle, Props>(
  function TypedSignature({ name, className }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [fontReady, setFontReady] = useState(false);

    useEffect(() => {
      // Wait until Dancing Script has loaded before painting — otherwise the
      // first render uses a fallback face and produces a different glyph set.
      let cancelled = false;
      const fontsApi = (
        document as unknown as {
          fonts?: {
            load: (s: string) => Promise<unknown>;
            ready?: Promise<unknown>;
          };
        }
      ).fonts;
      if (!fontsApi) {
        setFontReady(true);
        return;
      }
      fontsApi
        .load(`700 48px "Dancing Script"`)
        .then(() => {
          if (!cancelled) setFontReady(true);
        })
        .catch(() => {
          if (!cancelled) setFontReady(true);
        });
      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = Math.max(window.devicePixelRatio || 1, 3);
      const rect = canvas.getBoundingClientRect();
      const cssW = rect.width;
      const cssH = rect.height;
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);

      const value = name.trim();
      if (!value) return;

      // Auto-fit: shrink font size until the name fits horizontally within
      // 88% of the canvas width.
      let size = Math.floor(cssH * 0.62);
      ctx.fillStyle = TEXT_COLOR;
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      const maxWidth = cssW * 0.88;
      for (let i = 0; i < 16; i++) {
        ctx.font = `700 ${size}px ${FONT_FAMILY}`;
        const m = ctx.measureText(value);
        if (m.width <= maxWidth) break;
        size = Math.floor(size * 0.92);
        if (size < 14) break;
      }
      ctx.font = `700 ${size}px ${FONT_FAMILY}`;
      ctx.fillText(value, cssW / 2, cssH / 2);
    }, [name, fontReady]);

    useImperativeHandle(ref, () => ({
      clear() {
        // Clearing the typed mode just blanks the canvas; the name input is
        // owned by the parent.
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      },
      isEmpty() {
        return name.trim().length === 0;
      },
      toPngBytes() {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        if (name.trim().length === 0) return null;
        const dataUrl = canvas.toDataURL("image/png");
        const b64 = dataUrl.split(",")[1] ?? "";
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
      },
    }));

    return (
      <div className={className ?? "relative"}>
        <canvas
          ref={canvasRef}
          aria-label={`Typed signature for ${name || "your name"}`}
          className="block w-full h-44 rounded-lg border border-slate-300 bg-white"
          style={{ fontFamily: FONT_FAMILY }}
        />
        {!name.trim() ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Your typed signature will appear here
          </div>
        ) : null}
      </div>
    );
  },
);
